<template>
  <div class="chat-session-sidebar">
    <!-- Candidate Header -->
    <div class="candidate-header">
      <div class="candidate-info">
        <div class="candidate-avatar">
          {{ getInitials(selectedCandidate.name) }}
        </div>
        <div class="candidate-details">
          <h3>{{ selectedCandidate.name }}</h3>
          <p v-if="selectedCandidate.company" class="candidate-company">{{ selectedCandidate.company }}</p>
        </div>
      </div>
      <button class="back-btn" @click="goBackToSessions" title="Back to Sessions">
        <i class="las la-arrow-left"></i>
      </button>
    </div>

    <!-- Session Info -->
    <div class="session-info-section">
      <h4>Chat Session</h4>
      <div class="session-details">
        <p class="session-title">{{ chatSession.chat_header || 'Untitled Session' }}</p>
        <p class="session-date">{{ formatDate(chatSession.created_at) }}</p>
        

      </div>
    </div>

    

    <!-- Session Files -->
    <div class="session-files-section">
      <div class="section-header">
        <h4>Session Files ({{ sessionFiles.length }})</h4>
        <button class="add-file-btn" @click="openFileSelector" title="Add Document">
          <i class="las la-plus"></i>
        </button>
      </div>
      <div v-if="sessionFiles.length === 0" class="no-files">
        <p>No files added to this session yet.</p>
        <p class="hint">Add files to start chatting about them.</p>
      </div>
      <div v-else class="session-files-table-container">
        <div class="session-files-table">
          <div 
            v-for="file in sessionFiles" 
            :key="file.id" 
            class="session-files-table-row"
            :class="{ 'selected': selectedFile && selectedFile.id === file.id }"
            @click="selectFile(file)"
          >
            <div class="file-icon">
              <i class="las la-file-pdf"></i>
            </div>
            <div class="file-info">
              <div class="file-name">{{ file.file_name || file.name }}</div>
            </div>
            <div class="file-actions">
              <button 
                class="remove-file-btn" 
                @click.stop="removeFile(file.id)"
                :disabled="removingFileId === file.id"
                :title="removingFileId === file.id ? 'Removing...' : 'Remove from session'"
              >
                <BaseSpinner v-if="removingFileId === file.id" size="small" />
                <i v-else class="las la-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions">
      <h5>Quick Actions</h5>
      <div class="action-buttons">
        <button class="action-btn" @click="generateReport" title="Generate Report" :disabled="generatingReport">
          <BaseSpinner v-if="generatingReport" size="small" />
          <i v-else class="las la-chart-bar"></i>
          {{ generatingReport ? 'Generating...' : 'Generate Report' }}
        </button>
        <button class="action-btn" @click="exportChat" title="Export Chat" :disabled="exportingChat">
          <BaseSpinner v-if="exportingChat" size="small" />
          <i v-else class="las la-download"></i>
          {{ exportingChat ? 'Exporting...' : 'Export Chat' }}
        </button>
      </div>
    </div>

    <!-- File Selector Modal -->
    <div v-if="showFileSelector" class="file-selector-modal">
      <div class="modal-overlay" @click="closeFileSelector"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h4>Select Files to Add</h4>
          <div class="modal-header-actions">
            <button class="close-btn" @click="closeFileSelector">
              <i class="las la-times"></i>
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div v-if="loadingFiles" class="loading-files">
            <BaseSpinner size="small" />
            <p>Loading available files...</p>
          </div>
          <div v-else-if="availableFiles.length === 0" class="no-available-files">
            <p>No files available to add.</p>
            <button class="upload-new-btn" @click="goToUpload">
              <i class="las la-upload"></i>
              Upload New File
            </button>
          </div>
          <div v-else class="files-table-container">
            <div class="files-table-header">
              <label class="select-all-checkbox">
                <input 
                  type="checkbox" 
                  :checked="allFilesSelected" 
                  @change="toggleSelectAll"
                />
                <span>Select All</span>
              </label>
              <button 
                class="add-selected-btn" 
                @click="addSelectedFiles"
                :disabled="selectedFiles.length === 0 || addingFiles"
              >
                <BaseSpinner v-if="addingFiles" size="small" />
                <i v-else class="las la-plus"></i>
                {{ addingFiles ? 'Adding...' : `Add Selected (${selectedFiles.length})` }}
              </button>
            </div>
            <div class="files-table">
              <div 
                v-for="file in availableFiles" 
                :key="file.id" 
                class="files-table-row"
                :class="{ 'selected': isFileSelected(file.id) }"
                @click="toggleFileSelection(file)"
              >
                <label class="file-checkbox" @click.stop>
                  <input 
                    type="checkbox" 
                    :checked="isFileSelected(file.id)"
                    @change="toggleFileSelection(file)"
                  />
                </label>
                <div class="file-icon">
                  <i class="las la-file-pdf"></i>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.file_name || file.name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";

export default {
  name: 'ChatSessionSidebar',
  components: {
    BaseSpinner
  },
  props: {
    selectedCandidate: {
      type: Object,
      required: true
    },
    chatSession: {
      type: Object,
      required: true
    },
    sessionFiles: {
      type: Array,
      default: () => []
    },
    selectedFile: {
      type: Object,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showFileSelector: false,
      availableFiles: [],
      selectedFiles: [],
      loadingFiles: false,
      generatingReport: false,
      removingFileId: null,
      exportingChat: false,
      selectingAll: false,
      addingFiles: false
    }
  },
  computed: {
    allFilesSelected() {
      return this.availableFiles.length > 0 && 
             this.selectedFiles.length === this.availableFiles.length;
    }
  },
  methods: {
    async openFileSelector() {
      this.showFileSelector = true;
      await this.loadAvailableFiles();
    },
    
    closeFileSelector() {
      this.showFileSelector = false;
      this.availableFiles = [];
      this.selectedFiles = [];
    },
    
    async loadAvailableFiles() {
      this.loadingFiles = true;
      try {
        // Get all files for the candidate
        const files = await ChatbotService.getUploads(this.selectedCandidate.id);
        let allFiles = Array.isArray(files) ? files : (files.files || files.data || []);
        allFiles = allFiles.filter(file => file.type === 'bank_statement');
        
        // Filter out files that are already in the session
        this.availableFiles = allFiles.filter(file => 
          !this.sessionFiles.some(sessionFile => sessionFile.id === file.id)
        );
      } catch (err) {
        console.error('Error loading available files:', err);
        this.availableFiles = [];
      } finally {
        this.loadingFiles = false;
      }
    },
    
    isFileSelected(fileId) {
      return this.selectedFiles.some(f => f.id === fileId);
    },
    
    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.id === file.id);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
      } else {
        this.selectedFiles.push(file);
      }
    },
    
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedFiles = [...this.availableFiles];
      } else {
        this.selectedFiles = [];
      }
    },
    
    async addSelectedFiles() {
      if (this.selectedFiles.length === 0) return;
      
      this.addingFiles = true;
      try {
        this.$emit('files-added', this.selectedFiles);
        this.closeFileSelector();
      } catch (error) {
        console.error('Error adding selected files:', error);
        this.$bvToast.toast('Error adding files: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.addingFiles = false;
      }
    },
    
    async removeFile(fileId) {
      this.removingFileId = fileId;
      try {
        this.$emit('file-removed', fileId);
      } finally {
        // Reset loading state after a short delay to show the spinner
        setTimeout(() => {
          this.removingFileId = null;
        }, 500);
      }
    },
    
    selectFile(file) {
      this.$emit('file-selected', file);
    },
    
    async generateReport() {
      if (!this.selectedFile) {
        this.$bvToast.toast('Please select a file first', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      console.log('Generating report for file:', this.selectedFile);
      const fileId = this.selectedFile.id || this.selectedFile.file_id || this.selectedFile.name;
      if (!fileId) {
        this.$bvToast.toast('Error: Could not determine file ID', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.generatingReport = true;
      try {
        const blob = await ChatbotService.generateReport(fileId);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.selectedFile.file_name || this.selectedFile.name || 'report'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating report:', error);
        this.$bvToast.toast('Error generating report: ' + error.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generatingReport = false;
      }
    },
    
    async exportChat() {
      if (!this.chatSession || !this.chatSession.id) {
        this.$bvToast.toast('Error: No chat session available', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.exportingChat = true;
      try {
        // Show loading state
        this.$bvToast.toast('Exporting chat conversation...', {
          title: 'Processing',
          variant: 'info',
          solid: true,
          autoHideDelay: 2000
        });

        // Create chat export with actual messages
        const chatExport = this.createChatExport();
        
        // Create and download the chat export
        const blob = new Blob([chatExport], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Chat_Export_${this.selectedCandidate.name}_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$bvToast.toast('Chat conversation exported successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });

      } catch (error) {
        console.error('Error exporting chat:', error);
        this.$bvToast.toast('Error exporting chat: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.exportingChat = false;
      }
    },

    createChatExport() {
      const exportContent = [
        'CHAT SESSION EXPORT',
        '===================',
        '',
        `Candidate: ${this.selectedCandidate.name}`,
        this.selectedCandidate.company ? `Company: ${this.selectedCandidate.company}` : '',
        `Session: ${this.chatSession.chat_header || 'Untitled Session'}`,
        `Date: ${new Date(this.chatSession.created_at).toLocaleDateString()}`,
        `Files: ${this.sessionFiles.length}`,
        '',
        'CHAT CONVERSATION:',
        '==================',
        ''
      ];

      // Add chat messages
      if (this.messages && this.messages.length > 0) {
        this.messages.forEach((message, index) => {
          const role = message.role === 'user' ? 'USER' : 'ASSISTANT';
          const timestamp = message.timestamp ? new Date(message.timestamp).toLocaleString() : '';
          
          exportContent.push(
            `${role}${timestamp ? ` (${timestamp})` : ''}:`,
            message.content,
            ''
          );
        });
      } else {
        exportContent.push('No messages in this session yet.');
      }

      exportContent.push(
        'SESSION FILES:',
        '-------------'
      );

      if (this.sessionFiles.length > 0) {
        this.sessionFiles.forEach((file, index) => {
          exportContent.push(
            `${index + 1}. ${file.file_name || file.name}`,
            `   Type: ${file.document_type || 'PDF'}`,
            `   Uploaded: ${new Date(file.uploaded_at || file.created_at).toLocaleDateString()}`,
            ''
          );
        });
      } else {
        exportContent.push('No files added to this session.');
      }

      exportContent.push(
        'REPORT GENERATED:',
        `Date: ${new Date().toLocaleDateString()}`,
        `Time: ${new Date().toLocaleTimeString()}`
      );

      return exportContent.filter(line => line !== '').join('\n');
    },
    
    goToUpload() {
      this.$router.push({ 
        name: 'chatbot-upload',
        params: { 
          candidateId: this.selectedCandidate.id
        }
      });
    },
    
    goBackToSessions() {
      this.$router.push({ 
        name: 'chatbot-candidate-sessions',
        params: { candidateId: this.selectedCandidate.id }
      });
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
      } catch (e) {
        return '';
      }
    },
    
    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'status-active';
        case 'completed':
          return 'status-completed';
        case 'paused':
          return 'status-paused';
        default:
          return 'status-active';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.chat-session-sidebar {
  width: 325px;
  background: #ffffff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.candidate-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.candidate-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.candidate-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
  color: $color-secondary;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.candidate-details h3 {
  margin: 0 0 0.25rem 0;
  font-size: 1.2rem;
  color: #2d3748;
  font-family: $brand-font-medium;
  line-height: 1.2;
  font-weight: 600;
}

.candidate-company {
  margin: 0;
  font-size: 0.9rem;
  color: #718096;
  font-family: $brand-font-regular;
}

.back-btn {
  background: none;
  color: #4a5568;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
  
  i {
    font-size: 0.9rem;
    color: #4a5568;
  }
}

.session-info-section,
.session-files-section,
.quick-actions {
  padding: 1.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.9rem;
  
  h4 {
    margin: 0;
  }
}

.session-files-section {
  padding-top: 5px;
}

.add-file-btn {
  margin-right: 0.25rem;
  background: $color-primary;
  color: $color-background;
  border: none;
  padding: 0.25rem;
  border-radius: 50%;
  font-family: $brand-font-medium;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: darken($color-primary, 10%);
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    background: #6c757d;
    color: #ffffff;
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
  
  i {
    font-size: 0.9rem;
    color: $color-background;
  }
}

.session-info-section {
  padding-top: 0;
}

.session-info-section h4,
.session-files-section h4,
.quick-actions h5 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #2d3748;
  font-family: $brand-font-medium;
  font-weight: 600;
}

.session-details {
  .session-title {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: #2d3748;
    font-weight: 500;
  }
  
  .session-date {
    margin: 0 0 1rem 0;
    font-size: 0.9rem;
    color: #718096;
  }
}


.session-files-section {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  .no-files {
    text-align: center;
    color: #718096;
    padding: 2rem 1rem;
    
    p {
      margin: 0 0 0.5rem 0;
      font-size: 0.9rem;
      
      .hint {
        font-size: 0.8rem;
        color: #a0aec0;
        font-style: italic;
      }
    }
  }
  h4{
    margin: 0;
  }
  
  .session-files-table-container {
    display: flex;
    flex-direction: column;
    max-height: 210px;
    overflow: hidden;
    margin-top: 0.5rem;
    background: #fafbfc;
    border: 1px solid #e9ecef;
    border-radius: 8px;
  }
  
  .session-files-table {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0.5rem;
    
    // Custom scrollbar styling
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(0, 0, 0, 0.2);
      }
    }
    
    // Firefox scrollbar styling
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
  }
  
  .session-files-table-row {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 0.5rem 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    transition: all 0.2s ease;
    cursor: pointer;
    
    &:hover {
      background: #edf2f7;
      border-color: $color-primary;
    }
    
    &.selected {
      background: rgba($color-primary, 0.1);
      border-color: $color-primary;
    }
    
    .file-icon {
      color: #e53e3e;
      font-size: 1.25rem;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      
      i {
        color: #e53e3e;
      }
    }
    
    .file-info {
      flex: 1;
      min-width: 0;
      
      .file-name {
        font-size: 0.9rem;
        color: #2d3748;
        font-weight: 500;
        line-height: 1.3;
        word-break: break-word;
      }
    }
    
    .file-actions {
      flex-shrink: 0;
      
      .remove-file-btn {
        background: none;
        color: #e53e3e;
        border: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        
        &:hover:not(:disabled) {
          background: #fed7d7;
          transform: scale(1.1);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        i {
          font-size: 0.9rem;
        }
      }
    }
  }
}

.quick-actions {
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .action-btn {
    width: 100%;
    background: #f7fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    padding: 0.75rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    &:hover:not(:disabled) {
      background: #edf2f7;
      border-color: $color-primary;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    i {
      font-size: 1rem;
    }
  }
}

// File Selector Modal
.file-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h4 {
    margin: 0;
    font-size: 1.2rem;
    color: #2d3748;
    font-weight: 600;
  }
  
  .modal-header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #718096;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f7fafc;
      color: #4a5568;
    }
  }
}

.modal-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-files {
  text-align: center;
  padding: 2rem;
  color: #718096;
  
  .base-spinner {
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1rem;
  }
}

.no-available-files {
  text-align: center;
  padding: 2rem;
  color: #718096;
  
  p {
    margin: 0 0 1rem 0;
    font-size: 1rem;
  }
  
  .upload-new-btn {
    background: $color-primary;
    color: $color-secondary;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      background: darken($color-primary, 10%);
    }
    
    i {
      font-size: 1rem;
    }
  }
}

.files-table-container {
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.files-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  padding-top:0;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 0.5rem;
  flex-shrink: 0;
  
  .select-all-checkbox {
    position: relative;
    top: 4px;
    margin-left: 13px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    
    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
      vertical-align: middle;
      background-color: #ffffff;
      border: 2px solid #cbd5e0;
      border-radius: 4px;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      position: relative;
      
      &:checked {
        background-color: $color-primary;
        border-color: $color-primary;
        
        &::after {
          content: '✓';
          position: absolute;
          width: 110%;
          height: 110%;
          color: white;
          border: none;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          text-align: center;
          margin-left: -1px;
          margin-top: 0px;
        }
      }
      
      &:hover {
        border-color: $color-primary;
      }
    }
  }
  
  .add-selected-btn {
    background: $color-primary;
    color: $color-secondary;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: $brand-font-medium;
    
    &:hover:not(:disabled) {
      background: darken($color-primary, 10%);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
    
    i {
      font-size: 1rem;
    }
  }
}

.files-table {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    transition: background 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
  
  // Firefox scrollbar styling
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}

.files-table-row {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #edf2f7;
    border-color: $color-primary;
  }
  
  &.selected {
    background: rgba($color-primary, 0.1);
    border-color: $color-primary;
  }
  
  .file-checkbox {
    flex-shrink: 0;
    position: relative;
    top: 4px;
    display: flex;
    align-items: center;
    
    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      margin: 0;
      vertical-align: middle;
      background-color: #ffffff;
      border: 2px solid #cbd5e0;
      border-radius: 4px;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      position: relative;
      
      &:checked {
        background-color: $color-primary;
        border-color: $color-primary;
        
        &::after {
          content: '✓';
          position: absolute;
          width: 110%;
          height: 110%;
          color: white;
          border: none;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          text-align: center;
          margin-left: -1px;
          margin-top: 0px;
        }
      }
      
      &:hover {
        border-color: $color-primary;
      }
    }
  }
  
  .file-icon {
    color: #e53e3e;
    font-size: 1.25rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
  
  .file-info {
    flex: 1;
    min-width: 0;
    
    .file-name {
      font-size: 0.9rem;
      color: #2d3748;
      font-weight: 500;
      line-height: 1.3;
      word-break: break-word;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .chat-session-sidebar {
    width: 100%;
    height: auto;
    max-height: 50vh;
  }
  
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}
</style> 